import React, { useCallback, useEffect, useState } from 'react';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Alert from 'react-bootstrap/Alert';
import Spinner from 'react-bootstrap/Spinner';
import Card from 'react-bootstrap/Card';
import { useParams } from 'react-router-dom';
import OcRequestStatusBadge from '../../components/OrganoidRequest/OcRequestStatusBadge';
import OcCandidateTable from '../../components/OrganoidRequest/OcCandidateTable';
import OcAttachmentsPanel from '../../components/OrganoidRequest/OcAttachmentsPanel';
import OcRequestCriteriaSummary from '../../components/OrganoidRequest/OcRequestCriteriaSummary';
import PublicOrganoidFormShell from '../../components/OrganoidRequest/PublicOrganoidFormShell';
import {
  getPublicOcRequest,
  patchPublicOcRequest,
} from '../../clients/oc_request_client';
import {
  buildPublicClientActionPayload,
  canShowPublicCandidates,
  getOcStageLabel,
} from '../../utils/ocRequest';
import { showToast } from '../../config_and_helpers/helpers';
import { logInfo } from '../../config_and_helpers/logger';

const LOG_CONTEXT = 'PublicOcRequestPortalPage';

/**
 * Token-gated client portal at /request/:token.
 */
const PublicOcRequestPortalPage = () => {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [comments, setComments] = useState('');
  const [decisions, setDecisions] = useState({});
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    const result = await getPublicOcRequest(token);
    setLoading(false);
    if (!result.success) {
      setError(result.messages?.[0] || 'Unable to load this request link.');
      setData(null);
      return;
    }
    setData(result.data);
    logInfo(LOG_CONTEXT, 'portal loaded', {
      requestNumber: result.data?.request_number,
      status: result.data?.status,
    });
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  const runAction = async (action, extra = {}) => {
    setBusy(true);
    const body = buildPublicClientActionPayload(action, {
      comments,
      ...extra,
    });
    const result = await patchPublicOcRequest(token, body);
    setBusy(false);
    if (result.success) {
      showToast('Update submitted', 'success', 'top-right', 3000);
      setComments('');
      setData(result.data);
    } else {
      showToast(result.messages?.[0] || 'Action failed', 'error', 'top-right', 5000);
    }
  };

  const handleConfirmSelection = () => {
    const candidate_decisions = Object.entries(decisions)
      .filter(([, decision]) => decision)
      .map(([candidate_id, decision]) => ({
        candidate_id: Number(candidate_id),
        decision,
      }));
    if (!candidate_decisions.length) {
      showToast('Select at least one candidate decision', 'warning', 'top-right', 3000);
      return;
    }
    runAction('confirm_client_selection', { candidate_decisions });
  };

  if (loading) {
    return (
      <div className="oc-page">
        <div className="text-center py-5">
          <Spinner animation="border" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <PublicOrganoidFormShell title="Unable to open link">
        <Alert variant="danger">{error}</Alert>
      </PublicOrganoidFormShell>
    );
  }

  const showCandidates = canShowPublicCandidates(data?.status);

  return (
    <PublicOrganoidFormShell
      title={data.request_number}
      subtitle={`${getOcStageLabel(data.current_stage)} · client portal`}
    >
      <div className="d-flex flex-wrap align-items-center gap-2 mb-3 justify-content-center">
        <OcRequestStatusBadge status={data.status} />
      </div>

      <Card className="oc-glass-panel mb-3 border-0">
        <Card.Header className="oc-glass-header">Your request</Card.Header>
        <Card.Body>
          <OcRequestCriteriaSummary request={data} />
        </Card.Body>
      </Card>

      {data.comments?.length ? (
        <Card className="oc-glass-panel mb-3 border-0">
          <Card.Header className="oc-glass-header">Messages from the lab</Card.Header>
          <Card.Body>
            {data.comments.map(c => (
              <div key={c.id} className="mb-2">
                <div className="small text-muted">
                  {c.created_at ? new Date(c.created_at).toLocaleString() : ''}
                </div>
                <div>{c.body}</div>
              </div>
            ))}
          </Card.Body>
        </Card>
      ) : null}

      {showCandidates ? (
        <Card className="oc-glass-panel mb-3 border-0">
          <Card.Header className="oc-glass-header">Proposed cultures</Card.Header>
          <Card.Body>
            <OcCandidateTable
              candidates={data.candidates || []}
              mode="public"
              decisions={decisions}
              onDecisionChange={(id, value) =>
                setDecisions(prev => ({ ...prev, [id]: value }))
              }
            />
            {data.status === 'AWAITING_CLIENT_CONFIRMATION' ? (
              <Button
                className="oc-primary-btn mt-2"
                disabled={busy}
                onClick={handleConfirmSelection}
              >
                Confirm selection
              </Button>
            ) : null}
          </Card.Body>
        </Card>
      ) : null}

      <Card className="oc-glass-panel mb-3 border-0">
        <Card.Body>
          <Form.Group className="mb-3">
            <Form.Label>Message to the lab</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={comments}
              onChange={e => setComments(e.target.value)}
            />
          </Form.Group>
          <div className="d-flex flex-wrap gap-2">
            {data.status === 'NEEDS_CLIENT_CLARIFICATION' ? (
              <Button
                className="oc-primary-btn"
                disabled={busy || !comments.trim()}
                onClick={() => runAction('client_submit_clarification')}
              >
                Submit clarification
              </Button>
            ) : null}
            {data.status === 'AWAITING_CLIENT_CONFIRMATION' ? (
              <>
                <Button
                  variant="outline-warning"
                  disabled={busy}
                  onClick={() => runAction('client_request_changes')}
                >
                  Request changes
                </Button>
                <Button
                  variant="outline-danger"
                  disabled={busy}
                  onClick={() => runAction('client_decline')}
                >
                  Decline request
                </Button>
              </>
            ) : null}
          </div>
        </Card.Body>
      </Card>

      <div className="oc-glass-panel">
        <div className="oc-glass-body">
          <OcAttachmentsPanel
            publicToken={token}
            attachments={data.attachments || []}
            onUpdated={() => load()}
          />
        </div>
      </div>
    </PublicOrganoidFormShell>
  );
};

export default PublicOcRequestPortalPage;
